// ADR-109 Rule RA-02 — apps/remote_assist/domMirror/capture.ts
//
// پیاده‌سازی MVP عمدی: به‌جای وارد کردن یک کتابخانه‌ی کامل rrweb (که در
// پروژه پیش‌تر استفاده نشده و توجیه اضافه‌کردنش برای این فاز اول روشن
// نیست)، یک پیاده‌سازی ساده‌ی MutationObserver + event listener نوشته شده
// که فقط subtree مشخص‌شده (معمولاً <main id="app-root">) را رصد می‌کند.
// این تصمیم صریحاً در ADR ثبت شده — اگر در آینده کیفیت مسیرسازی
// (fidelity) کافی نبود، مهاجرت به rrweb یک AM جداگانه خواهد بود.

import { applyDataMasking, isInputInjectionBlocked } from "./dataMask";
import type { DomPatchPayload, InputEventPayload } from "../types";

export interface StartCaptureOptions {
  root: HTMLElement;
  financialDataVisible: boolean;
  onPatch: (payload: DomPatchPayload) => void;
}

export function startEmployeeSideCapture(options: StartCaptureOptions): () => void {
  const { root, financialDataVisible, onPatch } = options;

  const emitFullSnapshot = () => {
    const masked = applyDataMasking(root, { financialDataVisible });
    onPatch({ html: masked.outerHTML, targetSelector: cssPathFor(root) });
  };

  const observer = new MutationObserver(() => {
    // Rule RA-02: هر تغییری در subtree، یک snapshot جدید (masked) می‌فرستد.
    // برای فاز اول عمداً diff واقعی محاسبه نمی‌شود — سادگی و صحت روی
    // بهینگی شبکه اولویت دارد؛ بهینه‌سازی incremental، AM بعدی است.
    emitFullSnapshot();
  });

  observer.observe(root, { childList: true, subtree: true, attributes: true, characterData: true });
  emitFullSnapshot();

  return () => observer.disconnect();
}

export function applyInputEventOnEmployeeSide(root: HTMLElement, payload: InputEventPayload): void {
  const target = root.querySelector<HTMLElement>(payload.targetSelector);
  if (!target) return;

  // Rule RA-08: صفحات/فیلدهای data-mask="always" هرگز رویداد خارجی نمی‌پذیرند.
  if (isInputInjectionBlocked(target)) return;

  if (payload.kind === "click") {
    target.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
    return;
  }

  if ((payload.kind === "input" || payload.kind === "change") && "value" in target) {
    (target as HTMLInputElement).value = payload.value ?? "";
    target.dispatchEvent(new Event(payload.kind, { bubbles: true }));
  }
}

/** Rule RA-02: مختصات نسبی، نه پیکسل مطلق — resolution/zoom طرفین لزوماً یکسان نیست. */
export function toRelativeCoordinates(clientX: number, clientY: number, viewport: { width: number; height: number }) {
  return {
    relativeX: clientX / viewport.width,
    relativeY: clientY / viewport.height,
  };
}

function cssPathFor(el: Element): string {
  if (el.id) return `#${el.id}`;
  return el.tagName.toLowerCase();
}
